import { useAuth } from "@clerk/nextjs";
import { ButtonVariant, CoButton } from "components/CoButton";
import MainDropdown from "components/CoDropdown/CoDropdown";
import { ShareDropdown } from "components/ShareDropdown";
import Tooltip from "components/Tooltip";
import path from "constants/path";
import { OpenAccessIcon } from "design_system";
import { useBookmarks, useSaveBookmarkModal } from "features/Bookmarks";
import { FullPaperLibraryLink, LibraryIcon } from "features/libraries";
import { useCurrentThread } from "features/Thread/hooks/useCurrentThread";
import { Paper, PaperDetails } from "helpers/api";
import {
  BookmarkType,
  isPaperBookmarked,
  isUploadedPaperBookmarked,
} from "helpers/bookmark";
import copyText from "helpers/copyText";
import { SearchResultTextType } from "helpers/search";
import {
  MixpanelActionSource,
  MixpanelCiteType,
  MixpanelFullTextLinkType,
} from "helpers/services/mixpanel/events";
import useAnalytics from "hooks/useAnalytics";
import { ShareType } from "hooks/useAnalytics/types";
import useLabels from "hooks/useLabels";
import usePaperCite, { PaperCiteModal } from "hooks/usePaperCite";
import { usePdfAccessHierarchy } from "hooks/usePdfAccessHierarchy";
import { ArrowUpRight, Ellipsis, Bookmark, Quote, Link } from "lucide-react";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import classNames from "classnames";

type PropsType = {
  showSaveBtn?: boolean;
  showCiteBtn?: boolean;
  showShareBtn?: boolean;
  shareText?: string;
  shareTextType?: SearchResultTextType;
  shareLink?: string;
  item: Paper;
  searchPage: number;
  paperIndex: number;
  onClickSave?: (item: Paper) => void;
  /**
   * Callback for when the cite button is clicked to open the cite modal
   */
  onClickCite?: (item: Paper) => void;
  /**
   * Callback for when the paper is cited with a specific citation type within the cite modal
   */
  onCitePaper?: (
    item: Paper,
    citeType: MixpanelCiteType,
    index: number,
    resultType?: SearchResultTextType
  ) => void;
  /**
   * Callback for when the full text link is clicked
   */
  onFullTextClick?: (
    paper: Paper | PaperDetails,
    linkType: MixpanelFullTextLinkType,
    actionSource: MixpanelActionSource,
    index?: number,
    libraryId?: string
  ) => void;
  sharePaperEventOverride?: (
    item: Paper,
    index: number,
    shareType: string,
    resultType?: SearchResultTextType
  ) => void;
  onActionTriggered?: () => void;
  noSocialSharing?: boolean;
  mixpanelActionSource?: MixpanelActionSource;
  asDropdown?: boolean;
  hideFullPaperLibraryLink?: boolean;
  className?: string;
};

const SearchResultActionsButtons = ({
  showSaveBtn,
  showCiteBtn,
  showShareBtn,
  noSocialSharing,
  searchLabels,
  shareLabels,
  isBookmarked,
  handleSavePaper,
  handleClickCiteItem,
  handleClickShareItem,
  handleClickCopyLink,
  shareTextType,
  shareText,
  shareLink,
  handleMixpanelShareEvent,
}: {
  showSaveBtn: boolean;
  showCiteBtn: boolean;
  showShareBtn: boolean;
  noSocialSharing: boolean;
  searchLabels: Record<string, string>;
  shareLabels: Record<string, string>;
  isBookmarked: boolean;
  handleSavePaper: () => void;
  handleClickCiteItem: () => void;
  handleClickShareItem: () => void;
  handleClickCopyLink: () => void;
  shareTextType: SearchResultTextType | undefined;
  shareText: string | undefined;
  shareLink: string | undefined;
  handleMixpanelShareEvent: (shareType: ShareType) => void;
}) => (
  <div
    className="flex justify-end items-center"
    onClick={(e) => {
      // Clicking on actions should not trigger a click on the overall search result
      e.stopPropagation();
    }}
  >
    {showSaveBtn ? (
      <Tooltip
        content={searchLabels["save-paper"]}
        closeDelay={0}
        closeDuration={0}
      >
        <CoButton
          variant={ButtonVariant.Flat}
          size="sm"
          onClick={handleSavePaper}
          icon={
            <Bookmark size={20} fill={isBookmarked ? "currentColor" : "none"} />
          }
        />
      </Tooltip>
    ) : undefined}

    {showCiteBtn ? (
      <Tooltip
        content={searchLabels["cite-paper"]}
        closeDelay={0}
        closeDuration={0}
      >
        <CoButton
          variant={ButtonVariant.Flat}
          size="sm"
          onClick={handleClickCiteItem}
          icon={<Quote size={20} />}
        />
      </Tooltip>
    ) : undefined}

    {showShareBtn && !noSocialSharing ? (
      <ShareDropdown
        answer={
          shareTextType === SearchResultTextType.ANSWER ? shareText : undefined
        }
        keyTakeaway={
          shareTextType === SearchResultTextType.KEY_TAKEAWAY
            ? shareText
            : undefined
        }
        description={shareLabels["share-paper-result-description"]}
        link={shareLink}
        title={shareLabels["share-paper"]}
        sharePaperEventOverride={(shareType) => {
          handleMixpanelShareEvent(shareType);
        }}
      >
        <Tooltip
          content={searchLabels["share-paper"]}
          closeDelay={0}
          closeDuration={0}
        >
          <CoButton
            variant={ButtonVariant.Flat}
            size="sm"
            onClick={(e) => {
              handleClickShareItem();
            }}
            icon={<Link size={20} />}
          />
        </Tooltip>
      </ShareDropdown>
    ) : showShareBtn && noSocialSharing ? (
      <Tooltip
        content={searchLabels["share-paper"]}
        closeDelay={0}
        closeDuration={0}
      >
        <CoButton
          variant={ButtonVariant.Flat}
          size="sm"
          onClick={(e) => {
            handleClickCopyLink();
            handleClickShareItem();
          }}
          icon={<Link size={20} />}
        />
      </Tooltip>
    ) : undefined}
  </div>
);

const SearchResultActionDropdown = ({
  showSaveBtn,
  showCiteBtn,
  showShareBtn,
  searchLabels,
  isBookmarked,
  handleSavePaper,
  handleClickCiteItem,
  handleClickCopyLink,
  item,
  handleClickFullTextLink,
}: {
  showSaveBtn: boolean;
  showCiteBtn: boolean;
  showShareBtn: boolean;
  searchLabels: Record<string, string>;
  isBookmarked: boolean;
  handleSavePaper: () => void;
  handleClickCiteItem: () => void;
  handleClickCopyLink: () => void;
  item: Paper;
  handleClickFullTextLink: (linkType: MixpanelFullTextLinkType) => void;
}) => {
  const dropdownItems = [];

  // Add full text option if available
  const { option, url, label, icon } = usePdfAccessHierarchy(item);
  if (option && url) {
    dropdownItems.push({
      key: `full-text-${option}`,
      onClick: () => {
        // Track the full text click event
        const linkType =
          option === "library"
            ? MixpanelFullTextLinkType.LIBRARY
            : option === "open-access"
              ? MixpanelFullTextLinkType.PDF
              : MixpanelFullTextLinkType.DOI;
        handleClickFullTextLink(linkType);
        window.open(url, "_blank");
      },
      leftIcon: (
        <>
          <ArrowUpRight size={20} className="text-xl icon-external-link" />
          {option === "library" && <LibraryIcon includeTooltip={false} />}
          {option === "open-access" && <OpenAccessIcon />}
        </>
      ),
      title: label,
      href: url,
      target: "_blank",
    });
  }

  if (showSaveBtn) {
    dropdownItems.push({
      key: "save",
      onClick: handleSavePaper,
      leftIcon: (
        <i
          className={
            isBookmarked
              ? "text-xl icon-bookmark-solid"
              : "text-xl icon-bookmark"
          }
        />
      ),
      title: searchLabels["save-paper"],
    });
  }

  if (showCiteBtn) {
    dropdownItems.push({
      key: "cite",
      onClick: handleClickCiteItem,
      leftIcon: <i className="text-xl icon-quote" />,
      title: searchLabels["cite-paper"],
    });
  }

  if (showShareBtn) {
    dropdownItems.push({
      key: "share",
      onClick: handleClickCopyLink,
      leftIcon: <i className="text-xl icon-link" />,
      title: searchLabels["share-paper"],
    });
  }

  return (
    <MainDropdown items={dropdownItems}>
      <CoButton variant={ButtonVariant.Flat} size="sm" icon={<Ellipsis />} />
    </MainDropdown>
  );
};

export const SearchResultActions = ({
  showSaveBtn = true,
  showCiteBtn = true,
  showShareBtn = true,
  noSocialSharing = false,
  shareText,
  shareTextType,
  shareLink,
  item,
  searchPage,
  paperIndex,
  onActionTriggered,
  mixpanelActionSource,
  onClickSave,
  onClickCite,
  onCitePaper,
  onFullTextClick,
  sharePaperEventOverride,
  asDropdown = false,
  hideFullPaperLibraryLink = false,
  className,
}: PropsType & { asDropdown?: boolean }) => {
  const router = useRouter();
  const { isSignedIn } = useAuth();
  const [shareLabels, searchLabels] = useLabels(
    "share",
    "screens.search",
    "research-hub.uploading-state"
  );
  const { bookmarkItems } = useBookmarks();

  const {
    setAnalyticResult,
    setAnalyticActionSource,
    clickShareEvent,
    executeShareEvent,
    clickFullTextLinkEvent,
  } = useAnalytics();

  const {
    citeData,
    isCiteModalOpen,
    openCiteModal,
    closeCiteModal,
    downloadCitedPaperRis,
  } = usePaperCite();

  const {
    openSaveBookmarkModalForPaper,
    openSaveBookmarkModalForUploadedPaper,
  } = useSaveBookmarkModal();

  const { thread } = useCurrentThread() ?? {};

  const [isBookmarked, setIsBookmarked] = useState(false);

  const { paper_id } = item;

  useEffect(() => {
    if (paper_id.includes("u:")) {
      setIsBookmarked(isUploadedPaperBookmarked(paper_id, bookmarkItems));
    } else {
      setIsBookmarked(isPaperBookmarked(paper_id, bookmarkItems));
    }
  }, [setIsBookmarked, bookmarkItems, paper_id]);

  const handleClickSave = (
    bookmarkType: BookmarkType,
    searchItem: Paper | null
  ) => {
    if (
      mixpanelActionSource &&
      (bookmarkType === BookmarkType.PAPER ||
        bookmarkType === BookmarkType.UPLOADED_PAPER) &&
      searchItem !== null
    ) {
      setAnalyticActionSource(mixpanelActionSource);

      if (bookmarkType === BookmarkType.UPLOADED_PAPER) {
        openSaveBookmarkModalForUploadedPaper(searchItem.paper_id);
      } else {
        openSaveBookmarkModalForPaper(searchItem.paper_id, thread);
      }
    }

    if (onClickSave && searchItem) {
      onClickSave(searchItem);
    }
  };

  const handleSavePaper = () => {
    if (!isSignedIn) {
      router.push(
        `${path.SIGN_IN}#/?redirect_url=${encodeURIComponent(
          `${router?.asPath}`
        )}`
      );
      return;
    }

    handleClickSave(
      item.paper_id.includes("u:")
        ? BookmarkType.UPLOADED_PAPER
        : BookmarkType.PAPER,
      item
    );

    if (onActionTriggered) {
      onActionTriggered();
    }
  };

  const handleClickCiteItem = () => {
    if (mixpanelActionSource) {
      setAnalyticResult({ index: paperIndex, page: searchPage, item });
      setAnalyticActionSource(mixpanelActionSource);
      openCiteModal(item);
    }

    if (onClickCite) {
      onClickCite(item);
    }

    if (onActionTriggered) {
      onActionTriggered();
    }
  };

  const handleClickShareItem = () => {
    if (mixpanelActionSource) {
      setAnalyticResult({ index: paperIndex, page: searchPage, item });
      setAnalyticActionSource(mixpanelActionSource);
      clickShareEvent();
    }
  };

  const handleMixpanelShareEvent = (shareType: ShareType) => {
    if (sharePaperEventOverride) {
      sharePaperEventOverride(item, paperIndex, shareType, shareTextType);
    } else {
      executeShareEvent(shareType);
    }
  };

  const handleClickCopyLink = () => {
    if (shareLink) {
      copyText(shareLink);
      toast.success(shareLabels["link-copied"]);
      // Delay the analytics event to ensure redux state is updated with the
      // correct result data
      setTimeout(() => handleMixpanelShareEvent(ShareType.COPY_LINK), 10);
      handleClickShareItem();
    }
  };

  const handleClickFullTextLink = (linkType: MixpanelFullTextLinkType) => {
    const actionSource =
      mixpanelActionSource ?? MixpanelActionSource.SearchResults;
    setAnalyticResult({ index: paperIndex, page: searchPage, item });
    setAnalyticActionSource(actionSource);
    clickFullTextLinkEvent(linkType);
  };

  return (
    <div className={classNames("flex justify-end items-center", className)}>
      {asDropdown ? (
        <SearchResultActionDropdown
          showSaveBtn={showSaveBtn}
          showCiteBtn={showCiteBtn}
          showShareBtn={showShareBtn}
          searchLabels={searchLabels}
          isBookmarked={isBookmarked}
          handleSavePaper={handleSavePaper}
          handleClickCiteItem={handleClickCiteItem}
          handleClickCopyLink={handleClickCopyLink}
          item={item}
          handleClickFullTextLink={handleClickFullTextLink}
        />
      ) : (
        <SearchResultActionsButtons
          showSaveBtn={showSaveBtn}
          showCiteBtn={showCiteBtn}
          showShareBtn={showShareBtn}
          noSocialSharing={noSocialSharing}
          searchLabels={searchLabels}
          shareLabels={shareLabels}
          isBookmarked={isBookmarked}
          handleSavePaper={handleSavePaper}
          handleClickCiteItem={handleClickCiteItem}
          handleClickShareItem={handleClickShareItem}
          handleClickCopyLink={handleClickCopyLink}
          shareTextType={shareTextType}
          shareText={shareText}
          shareLink={shareLink}
          handleMixpanelShareEvent={handleMixpanelShareEvent}
        />
      )}
      <PaperCiteModal
        data={citeData}
        open={isCiteModalOpen}
        onClose={closeCiteModal}
        downloadRIS={downloadCitedPaperRis}
        citeEventOverride={(citeType) => {
          if (onCitePaper) {
            onCitePaper(item, citeType, paperIndex, shareTextType);
          }
        }}
      />
      {!hideFullPaperLibraryLink && !asDropdown && (
        <FullPaperLibraryLink
          item={item}
          variant={ButtonVariant.Tertiary}
          className="ml-2"
          actionSource={mixpanelActionSource}
          index={paperIndex}
          onFullTextClick={onFullTextClick}
        />
      )}
    </div>
  );
};
